"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DriveModeVoltageConditionValidationCheckType = exports.ConditionModeDenormalizedSchema = void 0;
exports.convertConditionModeSignalsFromIdsToNames = convertConditionModeSignalsFromIdsToNames;
exports.convertConditionModeSignalsFromNamesToIds = convertConditionModeSignalsFromNamesToIds;
exports.getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds = getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds;
exports.convertConditionModeToModeBase = convertConditionModeToModeBase;
exports.convertModeBaseToConditionModeDenormalized = convertModeBaseToConditionModeDenormalized;
exports.remapSignalIdsInModes = remapSignalIdsInModes;
const zod_1 = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
exports.ConditionModeDenormalizedSchema = zod_1.z.object({
    id: zod_1.z.string().describe('A unique id for this mode. Alphanumeric only.'),
    characteristic: zod_1.z.nativeEnum(sigma_engine_shared_2.BoundaryCharacteristic).describe('The characteristic being measured.'),
    minVal: zod_1.z
        .number()
        .nullable()
        .describe('The minimum characteristic value of the mode in base units. If not provided, there is no minimum. One or both of minVal and maxVal must be provided.'),
    nominalVal: zod_1.z
        .number()
        .nullable()
        .describe('The nominal (absolute) value of this condition, if stated as a single operating point, otherwise set to null.'),
    maxVal: zod_1.z
        .number()
        .nullable()
        .describe('The maximum charactistic value of the mode in base volts. If not provided, there is no maximum. One or both of minVal and maxVal must be provided.'),
    targetSignalId: zod_1.z.string().describe('The signal ID this mode applies to'),
    relativeSignalId: zod_1.z.string().describe('The signal ID that minVal and maxVal should be measured relative to'),
});
function convertConditionModeSignalsFromIdsToNames(modes, signals, logger) {
    return modes
        .map((mode) => {
        const targetSignalName = (0, sigma_engine_shared_1.findSignalNameBySignalId)(mode.targetSignalId, signals, logger);
        const relativeSignalName = (0, sigma_engine_shared_1.findSignalNameBySignalId)(mode.relativeSignalId, signals, logger);
        if (!targetSignalName || !relativeSignalName) {
            logger.warn('ConvertConditionModeSignalsFromIdsToNames_SignalIdNotFound', {
                modeId: mode.id,
                targetSignalId: mode.targetSignalId,
                targetSignalName,
                relativeSignalId: mode.relativeSignalId,
                relativeSignalName,
                availableSignalIds: signals.map((s) => s.id),
            });
            return null;
        }
        const { targetSignalId, relativeSignalId, ...rest } = mode;
        return {
            ...rest,
            targetSignal: targetSignalName,
            relativeSignal: relativeSignalName,
        };
    })
        .filter((mode) => mode !== null);
}
function convertConditionModeSignalsFromNamesToIds(modes, signals, logger) {
    return modes
        .map((mode) => {
        const targetSignalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(mode.targetSignal, signals, logger);
        const relativeSignalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(mode.relativeSignal, signals, logger);
        if (!targetSignalId || !relativeSignalId) {
            logger.warn('ConvertConditionModeSignalsFromNamesToIds_SignalNameNotFound', {
                modeId: mode.id,
                targetSignal: mode.targetSignal,
                targetSignalId,
                relativeSignal: mode.relativeSignal,
                relativeSignalId,
                availableSignalNames: signals.map((s) => s.name),
            });
            return null;
        }
        const { targetSignal, relativeSignal, ...rest } = mode;
        return {
            ...rest,
            targetSignalId,
            relativeSignalId,
        };
    })
        .filter((mode) => mode !== null);
}
function getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds(signals) {
    return exports.ConditionModeDenormalizedSchema.extend({
        id: zod_1.z.string().describe('A unique id for this mode. Alphanumeric only. Example: "vcc_1p3_4p2"'),
        characteristic: zod_1.z
            .literal(sigma_engine_shared_2.BoundaryCharacteristic.Voltage)
            .describe('The characteristic being measured. This is always "voltage".'),
        minVal: zod_1.z
            .number()
            .nullable()
            .describe('The minimum voltage of the mode in volts. If not provided, there is no minimum. One or both of minVal and maxVal must be provided.'),
        nominalVal: zod_1.z
            .number()
            .nullable()
            .describe('The nominal (absolute) value of this condition, if stated as a single operating point, otherwise set to null.'),
        maxVal: zod_1.z
            .number()
            .nullable()
            .describe('The maximum voltage of the mode in volts. If not provided, there is no maximum. One or both of minVal and maxVal must be provided.'),
        targetSignalId: zod_1.z
            .enum(signals.map((s) => s.id.toString()))
            .describe(exports.ConditionModeDenormalizedSchema.shape.targetSignalId.description),
        relativeSignalId: zod_1.z
            .enum(signals.map((s) => s.id.toString()))
            .describe(exports.ConditionModeDenormalizedSchema.shape.relativeSignalId.description),
    })
        .strict()
        .refine(assertVoltageModeHasMaxMinOrNominal, {
        message: 'Must have at least a minimum, nominal, or maximum',
    });
}
function assertVoltageModeHasMaxMinOrNominal(d) {
    return d.minVal !== undefined || d.maxVal !== undefined || d.nominalVal !== undefined;
}
function convertConditionModeToModeBase(mode, logger = (0, sigma_engine_shared_2.generateCompatibleConsoleLogger)()) {
    const { minVal, maxVal, nominalVal, ...d } = mode;
    let numerical;
    const addNominalIfPresent = (boundary) => {
        if (nominalVal !== null && nominalVal !== undefined) {
            return { ...boundary, nominalValue: nominalVal };
        }
        return boundary;
    };
    if (minVal !== undefined && minVal !== null && maxVal !== undefined && maxVal !== null) {
        if (nominalVal !== undefined && nominalVal !== null) {
            if (nominalVal < minVal || nominalVal > maxVal) {
                throw new Error(`Nominal value ${nominalVal} must be within the range [${minVal}, ${maxVal}] for mode ${mode.id}`);
            }
        }
        numerical = addNominalIfPresent({
            type: 'range',
            minVal,
            maxVal,
            characteristic: d.characteristic,
            minValIncluded: true,
            maxValIncluded: true,
        });
    }
    else if (minVal !== undefined && minVal !== null) {
        numerical = addNominalIfPresent({
            type: 'minimum',
            value: minVal,
            characteristic: d.characteristic,
            valInclusive: true,
        });
    }
    else if (maxVal !== undefined && maxVal !== null) {
        numerical = addNominalIfPresent({
            type: 'maximum',
            value: maxVal,
            characteristic: d.characteristic,
            valInclusive: true,
        });
    }
    else if (nominalVal !== undefined && nominalVal !== null) {
        numerical = {
            type: 'absolute',
            value: nominalVal,
            characteristic: d.characteristic,
        };
    }
    else {
        throw new Error('Need at least one of: minimum, maximum, or nominal value');
    }
    const boundaryClass = numerical.type === 'absolute' ?
        sigma_engine_shared_2.BoundaryClass.IC_ElectricalCharacteristics_MeasurementPoint
        : sigma_engine_shared_2.BoundaryClass.IC_ElectricalCharacteristics;
    const modeBase = {
        id: d.id,
        preconditions: [
            {
                id: 0,
                condition: {
                    boundary: {
                        type: sigma_engine_shared_2.BoundaryAssertionType.Numerical,
                        numerical,
                        target: { signal: d.targetSignalId },
                        relative_to: { signal: d.relativeSignalId },
                        class: boundaryClass,
                    },
                },
            },
        ],
    };
    const parseResult = sigma_engine_shared_2.ModeBaseSchema.safeParse(modeBase);
    if (!parseResult.success) {
        logger.warn('ConvertConditionModeToModeBase_ValidationFailed', {
            modeId: mode.id,
            errors: parseResult.error.flatten().fieldErrors,
        });
        return { id: d.id };
    }
    return parseResult.data;
}
function convertModeBaseToConditionModeDenormalized(mode, signals = [], restrictToCharacteristic, logger) {
    if (!mode.preconditions || mode.preconditions.length === 0) {
        return [];
    }
    const results = [];
    for (const precondition of mode.preconditions) {
        if ('mode' in precondition.condition) {
            logger?.warn('ConvertModeBaseToConditionModeDenormalized_ModeReferenceConditionSkipped', {
                modeId: mode.id,
                preconditionId: precondition.id,
                referencedMode: precondition.condition.mode,
            });
            continue;
        }
        if ('boundary' in precondition.condition) {
            const boundaryCondition = precondition.condition;
            const boundary = boundaryCondition.boundary;
            if (boundary.type === sigma_engine_shared_2.BoundaryAssertionType.Numerical && 'numerical' in boundary) {
                const numericalBoundary = boundary.numerical;
                if (restrictToCharacteristic !== undefined && numericalBoundary.characteristic !== restrictToCharacteristic) {
                    continue;
                }
                const targetSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.target);
                if (targetSignalIds.length === 0) {
                    throw new Error(`Expected at least one target signal ID, got 0 for mode ${mode.id}`);
                }
                const relativeSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.relative_to);
                if (relativeSignalIds.length !== 1) {
                    throw new Error(`Expected exactly one relative signal ID, got ${relativeSignalIds.length} for mode ${mode.id}`);
                }
                const relativeSignalId = relativeSignalIds[0].toString();
                let minVal = null;
                let maxVal = null;
                let nominalVal = null;
                switch (numericalBoundary.type) {
                    case 'range':
                        minVal = numericalBoundary.minVal;
                        maxVal = numericalBoundary.maxVal;
                        nominalVal = numericalBoundary.nominalValue ?? null;
                        break;
                    case 'minimum':
                        minVal = numericalBoundary.value;
                        nominalVal = numericalBoundary.nominalValue ?? null;
                        break;
                    case 'maximum':
                        maxVal = numericalBoundary.value;
                        nominalVal = numericalBoundary.nominalValue ?? null;
                        break;
                    case 'absolute':
                        nominalVal = numericalBoundary.value;
                        minVal = null;
                        maxVal = null;
                        break;
                    default:
                        throw new Error(`Unhandled boundary type: ${numericalBoundary.type}`);
                }
                const explodedEntries = targetSignalIds.map((targetSignalId, index) => ({
                    id: `${mode.id}__precondition_${precondition.id}__target_${index + 1}`,
                    characteristic: boundary.numerical.characteristic,
                    minVal,
                    maxVal,
                    nominalVal,
                    targetSignalId: targetSignalId.toString(),
                    relativeSignalId,
                }));
                results.push(...explodedEntries);
            }
        }
    }
    return results;
}
var DriveModeVoltageConditionValidationCheckType;
(function (DriveModeVoltageConditionValidationCheckType) {
    DriveModeVoltageConditionValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
    DriveModeVoltageConditionValidationCheckType["TargetSignalShouldNotBeMissing"] = "TargetSignalShouldNotBeMissing";
    DriveModeVoltageConditionValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
    DriveModeVoltageConditionValidationCheckType["ConditionShouldHaveAtLeastOneValue"] = "ConditionShouldHaveAtLeastOneValue";
})(DriveModeVoltageConditionValidationCheckType || (exports.DriveModeVoltageConditionValidationCheckType = DriveModeVoltageConditionValidationCheckType = {}));
const signal_id_remap_internal_1 = require("../signal-hygiene/signal-id-remap-internal");
function remapSignalIdsInModes(modes, signalIdMap, opts) {
    if (modes.length === 0) {
        return [];
    }
    const cloned = JSON.parse(JSON.stringify(modes));
    cloned.forEach((mode, i) => {
        const preconditions = mode.preconditions ?? [];
        preconditions.forEach((p, pi) => {
            const cond = p.condition;
            if (!cond || cond.type !== 'boundary' || !cond.boundary) {
                return;
            }
            (0, signal_id_remap_internal_1.remapBoundaryDefinition)(cond.boundary, signalIdMap, `modes[${i}].preconditions[${pi}].condition.boundary`, opts);
        });
    });
    return cloned;
}
//# sourceMappingURL=condition-mode-denormalized.js.map